import { useCallback, useEffect, useMemo, useState } from "react";

export type ColumnDef<T extends string> = {
  id: T;
  label: string;
  defaultVisible: boolean;
  /** Vždy zobrazeno, nelze vypnout (např. název, akce). */
  locked?: boolean;
};

function defaultVisibleIds<T extends string>(columns: ColumnDef<T>[]): T[] {
  return columns.filter((c) => c.defaultVisible || c.locked).map((c) => c.id);
}

function normalizeVisible<T extends string>(columns: ColumnDef<T>[], ids: T[]): T[] {
  const valid = new Set(columns.map((c) => c.id));
  const locked = columns.filter((c) => c.locked).map((c) => c.id);
  const ordered = columns.map((c) => c.id);
  const picked = new Set<T>([...locked, ...ids.filter((id) => valid.has(id))]);
  return ordered.filter((id) => picked.has(id));
}

/** Persistuje viditelné sloupce tabulky katalogu v localStorage. */
export function useCatalogColumns<T extends string>(storageKey: string, columns: ColumnDef<T>[]) {
  const [visible, setVisibleState] = useState<T[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return normalizeVisible(columns, JSON.parse(raw) as T[]);
    } catch {
      /* ignore */
    }
    return normalizeVisible(columns, defaultVisibleIds(columns));
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(visible));
  }, [storageKey, visible]);

  const setVisible = useCallback(
    (next: T[]) => setVisibleState(normalizeVisible(columns, next)),
    [columns]
  );

  const toggle = useCallback(
    (id: T) => {
      const col = columns.find((c) => c.id === id);
      if (col?.locked) return;
      setVisibleState((prev) => {
        if (prev.includes(id)) {
          const next = prev.filter((x) => x !== id);
          return next.length === 0 ? prev : normalizeVisible(columns, next);
        }
        return normalizeVisible(columns, [...prev, id]);
      });
    },
    [columns]
  );

  const isVisible = useCallback((id: T) => visible.includes(id), [visible]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => visible.includes(c.id)),
    [columns, visible]
  );

  return { visible, visibleColumns, toggle, isVisible, setVisible, columns };
}
